
export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  appSettings?: Partial<AppSettings>;
}

export interface HudlTeamReport {
  pointsWonOnServe: number;
  pointsLostOnServe: number;
  pointsWonOnReceive: number;
  pointsLostOnReceive: number;
}

export interface Organization {
  id: string;
  name: string;
  joinCode: string;
  role?: string; // The current user's role in this org
  isAdmin?: boolean; // Whether the current user is an org admin
  approvedAt?: string | null; // null = pending admin approval; undefined = column not yet queried
  isBetaTester?: boolean;
  betaApprovalDate?: string | null;
  betaExpiresAt?: string | null;
  matchCreditsUsed?: number;
  stripeSubscriptionStatus?: 'active' | 'trialing' | 'canceled' | 'none';
  unitPreference?: 'imperial' | 'metric'; // Org-wide default unit system
}

export const ROLE_LABELS: Record<string, string> = {
  head_coach: 'Head Coach',
  assistant_coach: 'Assistant Coach',
  assistant: 'Assistant Coach', // Legacy
  coach: 'Coach',
  team_manager: 'Team Manager',
  stat_keeper: 'Stat Keeper',
  parent: 'Parent',
  other: 'Other',
  player: 'Player', // Legacy
};

export const ASSIGNABLE_ROLES = [
  { value: 'head_coach', label: 'Head Coach' },
  { value: 'assistant', label: 'Assistant Coach' }, // stored as 'assistant' in DB for constraint compat
  { value: 'coach', label: 'Coach' },
  { value: 'team_manager', label: 'Team Manager' },
  { value: 'stat_keeper', label: 'Stat Keeper' },
  { value: 'parent', label: 'Parent' },
  { value: 'other', label: 'Other' },
];

export const COACH_ROLES = ['head_coach', 'assistant_coach', 'assistant', 'coach'];
export const STAFF_ROLES = [...COACH_ROLES, 'team_manager', 'stat_keeper'];

export interface NoteSection {
  id: string;
  title: string;
  content: string;
}

export interface Player {
  id: string;
  organizationId: string;
  teamId: string;
  name: string;
  jerseyNumber: string;
  position: string;
  photoUrl?: string; // Base64 string of the player's photo
  email?: string;
  phone?: string;
  parent1Name?: string;
  parent1Contact?: string;
  parent2Name?: string;
  parent2Contact?: string;
  goals?: string;
  notes?: string;
  rank?: number; // For depth chart sorting
  // New Fields
  gradYear?: string; // "2025", "Freshman", etc.
  heightCm?: number | null;  // Canonical — centimeters stored in DB
  weightKg?: number | null;  // Canonical — kilograms stored in DB
  height?: string;  // Legacy text column — read-only during migration
  weight?: string;  // Legacy text column — read-only during migration
  age?: number;
  handedness?: 'Right' | 'Left' | 'Ambidextrous';
  primaryServeType?: 'Float' | 'Topspin';
  secondaryPosition?: string;
  // Eligibility: Keys correspond to Team eligibilityRequirements
  eligibility?: Record<string, boolean>;
}

export interface Team {
  id: string;
  organizationId: string;
  name: string;
  ageGroup?: string;
  isDefault?: boolean;
  isFavorite?: boolean; // New field for favorited teams
  type?: 'club' | 'opponent'; // New field
  // Custom columns for the eligibility table
  eligibilityRequirements?: string[];
  // Coach Notes sections
  coachNotes?: NoteSection[];
  isArchived?: boolean;
}

export interface Season {
  id: string;
  organizationId: string;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  isActive: boolean;
  rosterSnapshot?: Player[] | null;
  createdAt?: string;
}

export interface GameEvent {
  id: string;
  organizationId: string;
  type: 'game' | 'practice' | 'other' | 'tournament';
  title: string;
  date: string;
  time: string;
  endTime?: string;
  location: string;
  opponent?: string;
  teamId?: string;
  tournamentId?: string;
  tournamentName?: string;
  // Optional stats container for aggregation if loaded
  stats?: StatRecord[];
}

/** Returns true for event types that are playable games (game or tournament) */
export const isGameType = (type: string) => type === 'game' || type === 'tournament';

export interface StatRecord {
  playerId: string;
  // Expanded stat types for granular tracking
  statType:
    | 'ace' | 'serve_attempt' | 'serve_error'
    | 'kill' | 'attack_error' | 'attack_attempt'
    | 'block_solo' | 'block_assist' | 'block_error'
    | 'dig' | 'dig_error'
    | 'assist' | 'ball_handling_error'
    | 'reception' | 'reception_error'
    | 'rotation_point' // For "PTS" (Rotation Points)
    | 'break_point'   // A point won by the serving team (used for BP% numerator)
    | 'server_point'  // A point won while this player was the server (used for PS%)
    | 'set_played'    // Sentinel added during Hudl import to mark sets played (no stat effect)
    | 'error'; // Generic error fallback
  timestamp: number;
  setNumber?: number; // To calculate Games Played (Sets)
  // New Context Fields
  phase?: 'serve' | 'receive';
  rotation?: string; // "1", "2", "3", "4", "5", "6" based on Setter position
  passRating?: number; // 0-3 for receptions
  // Serve detail tracking (added for AI suggestions)
  serveType?: 'jump' | 'float' | 'jump-float';
  serveZone?: 1 | 2 | 3 | 4 | 5 | 6; // Standard FIVB court zones on opponent's side
  // Reception detail (captured via post-pass overlay)
  incomingServeStyle?: 'topspin' | 'float' | 'jump' | 'jump-float'; // type of serve received
  contactType?: 'hands' | 'platform';       // how the ball was contacted
  // Voice-logging metadata (populated only when source === 'voice')
  setLocation?: 'outside' | 'middle' | 'right_side' | 'pipe' | 'back_row';
  attackLocation?: 'angle' | 'line' | 'tool' | 'blocked';
  attackType?: 'swing' | 'tip' | 'roll';
  attackErrorType?: 'out' | 'net' | 'blocked' | 'lift' | 'double' | 'under' | 'other';
  // Kill detail (captured via post-kill overlay)
  killHitType?: 'driven' | 'shot' | 'tip';
  killOutcome?: 'tool' | 'floor' | 'shank';
  // Libero-specific flags
  flags?: string[]; // e.g. ['libero_front_10ft'] when a libero assists in front of the attack line
  // Stat origin — used for source filter in Stats view
  source?: 'live' | 'import' | 'manual' | 'voice';
}

// Voice logging — parsed event returned by /api/gemini/voice-parse
export type VoiceParsedEvent =
  | {
      eventType: 'stat';
      statType: StatRecord['statType'];
      resolvedPlayerId: string;   // empty string = unresolved
      resolvedPlayerName: string;
      playerRef: string;          // raw text the user said
      team: 'us' | 'opponent';
      confidence: number;         // 0–1; <0.7 triggers ⚠️ flag in UI
      suggested?: boolean;        // true = auto-injected suggestion (not from transcript)
      // Optional metadata (voice-only)
      setLocation?: StatRecord['setLocation'];
      attackLocation?: StatRecord['attackLocation'];
      attackType?: StatRecord['attackType'];
      passQuality?: 'perfect' | 'good' | 'poor'; // maps to passRating 3/2/1
      serveType?: StatRecord['serveType'];
      serveTypeReceived?: StatRecord['incomingServeStyle'];
      contactType?: StatRecord['contactType'];
      serveZone?: StatRecord['serveZone'];
      serveZoneInferred?: boolean;
    }
  | {
      eventType: 'substitution';
      team: 'us' | 'opponent';
      playerInId: string;
      playerInRef: string;
      playerOutId: string;
      playerOutRef: string;
      confidence: number;
    }
  | {
      eventType: 'score';
      team: 'us' | 'opponent';
      points: number;             // almost always 1
      confidence: number;
    }
  | {
      eventType: 'rotation';
      team: 'us' | 'them';
      direction: 'cw' | 'ccw';  // cw = rotate forward (side-out direction), ccw = rotate back
      count: number;              // number of rotations (default 1)
      confidence: number;
    }
  | {
      eventType: 'libero_toggle';
      team: 'us' | 'them';
      liberoId: string;           // resolved libero player ID ('' if unresolvable)
      liberoRef: string;          // raw text used to reference the libero
      targetState: 'on_court' | 'off_court';
      confidence: number;
    }
  | {
      eventType: 'possession_flip';
      possession: 'serving' | 'receiving'; // from OUR team's perspective
      confidence: number;
    }
  | {
      eventType: 'timeout';
      team: 'us' | 'them';
      confidence: number;
    };

export interface VoiceParseResult {
  events: VoiceParsedEvent[];
  transcript?: string;          // what the model heard (audio mode) or was given (text mode)
  quotaStatus: {
    percentUsed: number;        // % of the org's monthly AI dollar budget used
    warning: 'moderate' | 'high' | 'critical' | null;
    spentUsd?: number;
    budgetUsd?: number;
  };
}

// PREMIUM FEATURE: In-game AI coaching suggestion (gated behind subscription paywall).
export interface GameSuggestion {
  category: 'serving' | 'offense' | 'setter' | 'defense';
  suggestion: string;  // Brief, actionable tip (1–2 sentences)
  rationale: string;   // Stat-backed explanation (2–3 sentences)
  playerName?: string; // Specific player this tip targets, if any
}

export interface LiberoAssignment {
  liberoId: string;
  replacesPlayerIds: string[]; // IDs of players this libero replaces in back row
  servesForPlayerId?: string; // ID of the specific player this libero serves for (if any)
}

export interface RotationalSubstitution {
  id: string;
  playerOffId: string;   // Player being subbed out (from active lineup)
  playerOnId: string;    // Player coming in from bench
  triggerType: 'front_row' | 'back_row' | 'after_serving' | 'rotation_number' | 'serve_receive_rotation';
  triggerRotation?: number;  // 1–6, only used for rotation_number and serve_receive_rotation
  ignoreFirstTime: boolean;
}

export interface Lineup {
  id: string;
  organizationId: string;
  teamId: string;
  name: string;
  positions: {
    p1: string;
    p2: string;
    p3: string;
    p4: string;
    p5: string;
    p6: string;
  };
  liberoAssignments?: LiberoAssignment[];
  rotationalSubstitutions?: RotationalSubstitution[];
  system?: '5-1' | '6-2';
  dualRoleSetterIds?: string[];
  isDefault?: boolean;
  isFavorite?: boolean;
  createdAt: string;
}

export interface GameState {
  id: string;
  opponent: string;
  ourScore: number;
  theirScore: number;
  ourSetsWon: number;
  theirSetsWon: number;
  set: number;
  lineup: {
    p1: string; // Right Back (Setter usually)
    p2: string; // Right Front (Right Side)
    p3: string; // Middle Front (Middle Blocker)
    p4: string; // Left Front (Outside Hitter)
    p5: string; // Left Back (Libero/MB)
    p6: string; // Middle Back (OH/MB)
  };
  numerals: {
    p1: string;
    p2: string;
    p3: string;
    p4: string;
    p5: string;
    p6: string;
  };
  opponentLineup: {
    op1: string; // Opponent Right Back
    op2: string; // Opponent Right Front
    op3: string; // Opponent Middle Front
    op4: string; // Opponent Left Front
    op5: string; // Opponent Left Back
    op6: string; // Opponent Middle Back
  };
  opponentNumerals: {
    op1: string;
    op2: string;
    op3: string;
    op4: string;
    op5: string;
    op6: string;
  };
  stats: StatRecord[];
  substitutionsUsed: {
    us: number;
    them: number;
  };
  // Team-level aggregate data from Hudl Match Report imports
  teamReport?: HudlTeamReport;
  // Runtime libero state (for game overrides)
  activeLiberos?: {
    us: LiberoAssignment[];
    them: LiberoAssignment[];
  };
  // Per-set scores in order: index 0 = Set 1, index 1 = Set 2, etc.
  setHistory?: Array<{ our: number; their: number }>;
  // Provenance for merged records — which two records were combined and how
  mergeOf?: {
    recordAId: string;
    recordBId: string;
    sources: Record<string, 'a' | 'b' | 'both'>;
    mergedAt: string;
  };
  // How this record was originally produced. Captured at edit time so a live
  // game that's later manually corrected still surfaces as "Live (edited)"
  // rather than collapsing to "Manual" once stats are rebuilt with source='manual'.
  originalSource?: 'live' | 'import' | 'manual';
}

export interface MatchRecord {
  id: string;
  eventId: string;
  organizationId: string;
  submittedBy: string | null;
  submittedByName: string;
  submittedAt: string;
  gameState: GameState;
  isRecord: boolean;
  teamId?: string;
  isCreditConsumed?: boolean;
}

export interface Drill {
  name: string;
  duration: string;
  focus: string;
  description: string;
  instructions: string[];
  suggestedBlock?: 'Warm Up' | 'Skill Development' | 'Team Play';
}

export interface StatThresholds {
  // Team Macro Metrics (stored as 0–1 decimals, displayed as %)
  sideoutPctGreen: number;      // % of serve-receive rallies won — green threshold
  sideoutPctAmber: number;      // yellow/warning threshold
  breakPointPctGreen: number;   // % of serve rallies won (points while serving) — green threshold
  breakPointPctAmber: number;
  // Team Micro Metrics
  passRatingGreen: number;      // Serve-receive pass quality (0–3 scale) — green threshold
  passRatingAmber: number;
  hitEfficiencyGreen: number;   // (Kills − Errors) / Attempts — green threshold (stored as decimal, e.g. 0.25 = 25%)
  hitEfficiencyAmber: number;
  servePctGreen: number;        // (Attempts − Errors) / Attempts — green threshold
  servePctAmber: number;
  killPctGreen: number;         // Kills / Attempts — green threshold
  killPctAmber: number;
  aceErrorRatioGreen: number;   // Aces ÷ Serve Errors — green threshold (ratio, not %)
  aceErrorRatioAmber: number;
}

export const DEFAULT_STAT_THRESHOLDS: StatThresholds = {
  sideoutPctGreen: 0.60,   sideoutPctAmber: 0.50,
  breakPointPctGreen: 0.35, breakPointPctAmber: 0.28,
  passRatingGreen: 2.4,    passRatingAmber: 1.6,
  hitEfficiencyGreen: 0.25, hitEfficiencyAmber: 0.15,
  servePctGreen: 0.90,     servePctAmber: 0.85,
  killPctGreen: 0.40,      killPctAmber: 0.30,
  aceErrorRatioGreen: 1.0, aceErrorRatioAmber: 0.5,
};

export const THRESHOLD_PRESETS: Record<string, { label: string; description: string; thresholds: StatThresholds }> = {
  youth: {
    label: 'Youth / Recreational',
    description: '12U–14U or beginners. Achievable targets for developing players.',
    thresholds: {
      sideoutPctGreen: 0.50,   sideoutPctAmber: 0.40,
      breakPointPctGreen: 0.25, breakPointPctAmber: 0.18,
      passRatingGreen: 1.8,    passRatingAmber: 1.3,
      hitEfficiencyGreen: 0.15, hitEfficiencyAmber: 0.05,
      servePctGreen: 0.80,     servePctAmber: 0.70,
      killPctGreen: 0.30,      killPctAmber: 0.20,
      aceErrorRatioGreen: 0.8, aceErrorRatioAmber: 0.4,
    },
  },
  junior: {
    label: 'Junior / Club',
    description: '14U–16U club or JV-level. Competitive but developing.',
    thresholds: {
      sideoutPctGreen: 0.55,   sideoutPctAmber: 0.45,
      breakPointPctGreen: 0.30, breakPointPctAmber: 0.23,
      passRatingGreen: 1.9,    passRatingAmber: 1.5,
      hitEfficiencyGreen: 0.20, hitEfficiencyAmber: 0.10,
      servePctGreen: 0.85,     servePctAmber: 0.78,
      killPctGreen: 0.35,      killPctAmber: 0.25,
      aceErrorRatioGreen: 0.9, aceErrorRatioAmber: 0.5,
    },
  },
  varsity: {
    label: 'Varsity / High School',
    description: '16U–18U varsity or competitive club. Industry-standard targets.',
    thresholds: { ...DEFAULT_STAT_THRESHOLDS },
  },
  college: {
    label: 'College / Elite',
    description: 'Collegiate or elite club. High-performance benchmarks.',
    thresholds: {
      sideoutPctGreen: 0.65,   sideoutPctAmber: 0.55,
      breakPointPctGreen: 0.40, breakPointPctAmber: 0.32,
      passRatingGreen: 2.2,    passRatingAmber: 1.8,
      hitEfficiencyGreen: 0.30, hitEfficiencyAmber: 0.20,
      servePctGreen: 0.92,     servePctAmber: 0.88,
      killPctGreen: 0.45,      killPctAmber: 0.35,
      aceErrorRatioGreen: 1.2, aceErrorRatioAmber: 0.7,
    },
  },
};

export interface InsightStats {
  attacking: boolean;
  serving: boolean;
  defense: boolean;
  blocking: boolean;
  setting: boolean;
  /** Per-metric overrides. Absent key = enabled (true). */
  metrics?: Record<string, boolean>;
}

export const DEFAULT_INSIGHT_STATS: InsightStats = {
  attacking: true,
  serving: true,
  defense: true,
  blocking: true,
  setting: true,
};

export interface AdvancedStatToggles {
  serveTypeTracking: boolean;
  killHitTypeRow: boolean;
  killOutcomeRow: boolean;
  errorDetailOverlay: boolean;
  serveReceiveDetail: boolean;
}

export const DEFAULT_ADVANCED_STAT_TOGGLES: AdvancedStatToggles = {
  serveTypeTracking: true,
  killHitTypeRow: true,
  killOutcomeRow: true,
  errorDetailOverlay: true,
  serveReceiveDetail: true,
};

export interface AppSettings {
  substitutionsPerSet: number;
  measurementSystem: 'metric' | 'imperial';
  allowTwoLiberos?: boolean;
  simpleStats?: boolean;
  advancedStatToggles?: Partial<AdvancedStatToggles>;
  statThresholds?: Partial<StatThresholds>;
  insightStats?: InsightStats;
  defaultOpponentServesFloat?: boolean;
}

export enum ViewState {
  DASHBOARD = 'DASHBOARD',
  TEAM = 'TEAM',
  GAME = 'GAME',
  STATS = 'STATS',
  PRACTICE = 'PRACTICE',
  SCHEDULE = 'SCHEDULE',
  ORGANIZATION = 'ORGANIZATION',
  SETTINGS = 'SETTINGS'
}
